import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AdminScopes, AdminUser, UserService } from '@app/shared/services/user.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HelpersService } from '@app/shared/services/helpers.service';
import { LoggerService } from '@app/shared/services/logger.service';
import { Observable, Subscription } from 'rxjs';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { StateService, TimeBucketInfoInterface } from '@app/shared/services/state.service';
import { OtpService } from '@app/shared/services/otp.service';
import {
  type BucketMetrics,
  HighVolumeRoute,
  RegionLocation,
  RegionMetricsResponse,
  RegionPerformanceResponse,
  TopPerformingRoute,
  TrendDirection,
} from '../../otp.interfaces';
import {
  CanadaHeatmapComponent,
  LocationData,
  ProvinceSelectionEvent,
} from '../canada-heat-map/canada-heat-map.component';
import { AccountTypesForOTP, ProvinceList, ShippingProviders } from '@app/shared/constants';
import { animate, style, transition, trigger } from '@angular/animations';

interface RouteClaimsData extends BucketMetrics {
  totalClaims: number;
  claimRate: number;
}

@Component({
  standalone: false,
  selector: 'app-otp-regions',
  templateUrl: './otp-regions.component.html',
  styleUrls: ['./otp-regions.component.scss'],
  animations: [
    trigger('fadeIn', [transition(':enter', [style({ opacity: 0 }), animate('300ms ease-in', style({ opacity: 1 }))])]),
    trigger('slideIn', [
      transition(':enter', [
        style({ transform: 'translateY(20px)', opacity: 0 }),
        animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
    trigger('expandCard', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('300ms ease-out', style({ height: '*', opacity: 1 })),
      ]),
    ]),
  ],
})
export class OtpRegionsComponent implements OnInit, OnDestroy {
  @ViewChild('heatMap') heatmapComponent: CanadaHeatmapComponent;

  // State management
  public timeBucket$ = this.stateService.timeBucketInfo$;
  private timeBucketSubscription: Subscription;
  public courierFilter$ = this.stateService.courierFilter$;
  private courierFilterSubscription: Subscription;
  public adminUserInfo$: Observable<any> = this.stateService.adminUserInfo$;
  private adminUserInfoSubscription: Subscription;

  // User permissions
  public canViewOtp: boolean = false;

  // Component state
  public loading: boolean = true;
  public error: string = '';
  public form: FormGroup | undefined;

  // Filter state
  public currentBucket: string;
  public currentHistory: number;
  public accountType: AccountTypesForOTP;
  public courierFilter: ShippingProviders[];
  public selectedOriginProvince: string = '';
  public selectedDestinationProvince: string = '';
  public provinceOptions: { id: string; text: string }[] = [];

  // API response data
  public regionData: RegionPerformanceResponse;

  // Heatmap data
  public locationData: LocationData[] = [];
  public detailedLocationData: LocationData[] = [];
  public selectedProvince: string | null = null;
  public locationCount: number = 0;

  // Route analysis data
  public topRoutes: TopPerformingRoute[] = [];
  public currentRouteMetrics: RegionMetricsResponse | null = null;
  public currentRoutePerformance: any[] = [];
  public showingRouteDetails: boolean = false;

  // Performance metrics
  public totalShipments: number = 0;
  public onTimeRate: number = 0;
  public averageDelay: number = 0;
  public totalClaims: number = 0;
  public claimRate: number = 0;

  // Courier performance by region
  public courierPerformanceData: any[] = [];
  public topPerformingCourier: string = '';
  public worstPerformingCourier: string = '';

  // Claims data presence
  public hasClaimsData: boolean = false;

  // Claims analysis data
  public highClaimRoutesByProvince: any[] = [];
  public provinceClaimStats = {
    totalClaims: 0,
    claimRate: 0,
    highRiskRoutesCount: 0,
  };

  // Color schemes for visualizations
  public defaultColorScheme: Color = {
    name: 'defaultScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#17a2b8', '#6f42c1'],
  };

  public performanceColorScheme: Color = {
    name: 'performanceScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#4caf50', '#8bc34a', '#ffeb3b', '#ff9800', '#f44336'],
  };

  public claimsColorScheme: Color = {
    name: 'claimsScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#FF9800', '#E91E63', '#9C27B0', '#3F51B5', '#2196F3'],
  };

  // Tooltip fields for heatmap
  public tooltipFields: { label: string; field: string }[] = [
    { label: 'Volume', field: 'volume' },
    { label: 'On-Time %', field: 'onTimePercentage' },
    { label: 'Avg Delay', field: 'averageDelay' },
    { label: 'Claims', field: 'totalClaims' },
    { label: 'Claim Rate', field: 'claimRate' },
  ];

  private static readonly PROVINCE_COORDINATES = {
    AB: { lat: 53.9333, lng: -116.5765 },
    BC: { lat: 53.7267, lng: -127.6476 },
    MB: { lat: 53.7609, lng: -98.8139 },
    NB: { lat: 46.5653, lng: -66.4619 },
    NL: { lat: 53.1355, lng: -57.6604 },
    NT: { lat: 64.8255, lng: -124.8457 },
    NS: { lat: 44.682, lng: -63.7443 },
    NU: { lat: 70.2998, lng: -83.1076 },
    ON: { lat: 51.2538, lng: -85.3232 },
    PE: { lat: 46.5107, lng: -63.4168 },
    QC: { lat: 52.9399, lng: -73.5491 },
    SK: { lat: 52.9399, lng: -106.4509 },
    YT: { lat: 64.2823, lng: -135.0 },
  };

  constructor(
    private formBuilder: FormBuilder,
    private helpersService: HelpersService,
    private logger: LoggerService,
    private userService: UserService,
    private stateService: StateService,
    private otpService: OtpService
  ) {}

  ngOnInit() {
    this.loading = true;
    this.adminUserInfoSubscription = this.adminUserInfo$.subscribe((adminUserInfo: AdminUser) => {
      this.canViewOtp = this.userService.hasAuthScope(adminUserInfo, [AdminScopes.VIEW_OTP]);
      if (this.canViewOtp) {
        this.initializeComponent();
      }
    });
  }

  ngOnDestroy() {
    this.adminUserInfoSubscription?.unsubscribe();
    this.timeBucketSubscription?.unsubscribe();
    this.courierFilterSubscription?.unsubscribe();
  }

  private initializeComponent() {
    // Create filter form
    this.createForm();

    // Subscribe to time bucket changes
    this.timeBucketSubscription = this.timeBucket$.subscribe((timeBucketInfo: TimeBucketInfoInterface | null) => {
      if (timeBucketInfo) {
        this.currentBucket = timeBucketInfo.timeBucket;
        this.currentHistory = timeBucketInfo.history || 1;
        this.accountType = timeBucketInfo.accountType;
        this.loadRegionData();
      }
    });

    // Subscribe to courier filter changes
    this.courierFilterSubscription = this.courierFilter$.subscribe((courierFilter: ShippingProviders[] | null) => {
      if (courierFilter) {
        this.courierFilter = courierFilter;
        if (this.currentBucket) {
          this.loadRegionData();
        }
      }
    });
  }

  private createForm() {
    this.form = this.formBuilder.group({
      originProvince: [this.selectedOriginProvince, []],
      destinationProvince: [this.selectedDestinationProvince, []],
    });

    // Set up the provinces list
    this.provinceOptions = ProvinceList;

    // Subscribe to form changes
    this.form.valueChanges.subscribe((values) => {
      if (
        values.originProvince !== this.selectedOriginProvince ||
        values.destinationProvince !== this.selectedDestinationProvince
      ) {
        // Update the stored filter values
        this.selectedOriginProvince = values.originProvince;
        this.selectedDestinationProvince = values.destinationProvince;

        // Update selected province when originProvince changes
        if (values.originProvince) {
          this.selectedProvince = values.originProvince;
        } else if (this.selectedProvince && !values.originProvince) {
          // Clear selected province if filter is cleared
          this.selectedProvince = null;
        }

        // Always refetch data when filters change
        this.loadRegionData();
      }
    });
  }

  private loadRegionData() {
    this.loading = true;
    this.error = '';

    const params: any = {
      timeBucket: this.currentBucket,
      history: this.currentHistory,
      accountType: this.accountType,
    };

    // Add filters if available
    if (this.selectedOriginProvince) {
      params.originProvince = this.selectedOriginProvince;
    }

    if (this.selectedDestinationProvince) {
      params.destinationProvince = this.selectedDestinationProvince;
    }

    this.otpService.getRegions(params).subscribe(
      (response) => {
        this.logger.log('GET Region Performance', response);
        this.regionData = response;
        this.processRegionData();
        this.loading = false;
      },
      (error) => {
        this.logger.error('GET Region Performance error', error);
        this.error = 'Failed to load region performance data';
        this.loading = false;
      }
    );
  }

  private processRegionData() {
    if (!this.regionData) return;

    // Process summary metrics
    this.processSummaryMetrics();

    // Process top routes
    this.processTopRoutes();

    // Process location data for heatmap
    this.processLocationData();

    // Process courier performance data
    this.processCourierPerformance();

    // Check for claims data
    this.checkClaimsData();
  }

  private initializeDefaultProvinceData() {
    // Add a minimal value to make provinces visible
    Object.entries(OtpRegionsComponent.PROVINCE_COORDINATES).forEach(([province, coords]) => {
      this.locationData.push({
        latitude: coords.lat,
        longitude: coords.lng,
        volume: 10, // Minimal volume to ensure visibility
        name: province,
        province: province,
      });
    });
  }

  private processSummaryMetrics() {
    // Get totals data from the response
    const totalsData = this.regionData.totals?.data || [];
    if (totalsData.length > 0) {
      const latestTotals = totalsData[totalsData.length - 1];
      this.totalShipments = latestTotals.totalShipments || 0;
      this.onTimeRate = latestTotals.onTimePercentage || 0;
      this.averageDelay = latestTotals.averageDelay || 0;

      // Add claims data if available
      if (latestTotals.totalClaims !== undefined) {
        this.totalClaims = latestTotals.totalClaims || 0;
        this.claimRate = latestTotals.claimRate || 0;
      }
    }
  }

  private processTopRoutes() {
    this.topRoutes = this.regionData.topPerformingRoutes || [];
  }

  private processLocationData() {
    // Reset location data
    this.locationData = [];

    // Create map data even if no metrics to ensure map displays
    if (!this.regionData?.metrics || this.regionData.metrics.length === 0) {
      // Use default data for Canadian provinces to ensure map renders
      this.initializeDefaultProvinceData();
      return;
    }

    // Create a map to aggregate shipment volumes by province
    const provinceVolumes = new Map<string, number>();
    const provinceMetrics = new Map<
      string,
      {
        onTimePercentage: number;
        averageDelay: number;
        totalClaims?: number;
        claimRate?: number;
      }
    >();

    // Process all origin-destination pairs to gather province data
    this.regionData.metrics.forEach((metric) => {
      const originProvince = metric.origin.province;
      const destinationProvince = metric.destination.province;

      // Get latest data point
      const latestData = metric.data[metric.data.length - 1];

      // Add to origin province volume
      if (provinceVolumes.has(originProvince)) {
        provinceVolumes.set(originProvince, provinceVolumes.get(originProvince) + latestData.totalShipments);
      } else {
        provinceVolumes.set(originProvince, latestData.totalShipments);
      }

      // Update metrics for provinces
      if (!provinceMetrics.has(originProvince)) {
        const metrics = {
          onTimePercentage: latestData.onTimePercentage,
          averageDelay: latestData.averageDelay,
        };

        if (latestData.claims) {
          metrics['totalClaims'] = latestData.claims.totalClaims;
          metrics['claimRate'] = latestData.claims.claimRate;
        }

        provinceMetrics.set(originProvince, metrics);
      }

      if (!provinceMetrics.has(destinationProvince)) {
        const metrics = {
          onTimePercentage: latestData.onTimePercentage,
          averageDelay: latestData.averageDelay,
        };

        if (latestData.claims) {
          metrics['totalClaims'] = latestData.claims.totalClaims;
          metrics['claimRate'] = latestData.claims.claimRate;
        }

        provinceMetrics.set(destinationProvince, metrics);
      }
    });

    // Create location data for the heatmap
    for (const [province, volume] of provinceVolumes.entries()) {
      if (OtpRegionsComponent.PROVINCE_COORDINATES[province]) {
        const metrics = provinceMetrics.get(province);
        const metadata = metrics
          ? {
              onTimePercentage: metrics.onTimePercentage,
              averageDelay: metrics.averageDelay,
              totalClaims: metrics.totalClaims,
              claimRate: metrics.claimRate,
            }
          : {};

        this.locationData.push({
          latitude: OtpRegionsComponent.PROVINCE_COORDINATES[province].lat,
          longitude: OtpRegionsComponent.PROVINCE_COORDINATES[province].lng,
          volume: volume,
          name: province,
          province: province,
          metadata: metadata,
        });
      }
    }
  }

  private processCourierPerformance() {
    // Process top performing couriers
    const topCouriers = this.regionData.topPerformingCouriers || [];

    // Create courier performance data for visualization
    this.courierPerformanceData = topCouriers
      .map((courier) => {
        const latestData = courier.data[courier.data.length - 1];

        const courierName = this.helpersService.convertShipmentProviderToString(courier.courier);

        return {
          name: courierName,
          value: latestData.performanceScore || 0,
          extra: {
            totalShipments: latestData.totalShipments || 0,
            claimRate: latestData.claimRate,
          },
        };
      })
      .sort((a, b) => b.value - a.value);

    // Set top and worst performing couriers
    if (this.courierPerformanceData.length > 0) {
      this.topPerformingCourier = this.courierPerformanceData[0].name;
      this.worstPerformingCourier = this.courierPerformanceData[this.courierPerformanceData.length - 1].name;
    }
  }

  private checkClaimsData() {
    // Check if claims data is available
    this.hasClaimsData = false;

    // Check in regions data
    if (this.regionData?.metrics) {
      this.hasClaimsData = this.regionData.metrics.some((region) => {
        return region.data.some((item) => item.claims && (item.claims.totalClaims > 0 || item.claims.claimRate > 0));
      });
    }

    // Also check in top routes
    if (!this.hasClaimsData && this.regionData?.topPerformingRoutes) {
      this.hasClaimsData = this.regionData.topPerformingRoutes.some((route) => {
        return route.data.some((item) => item.totalClaims > 0 || item.claimRate > 0);
      });
    }

    // Check in high claim routes
    if (!this.hasClaimsData && this.regionData?.highClaimRoutes) {
      this.hasClaimsData = this.regionData.highClaimRoutes.length > 0;
    }
  }

  /**
   * Event handlers for heatmap interactions
   */
  public onProvinceSelected(event: ProvinceSelectionEvent): void {
    this.selectedProvince = event.province;

    // Count locations in this province
    this.locationCount = this.locationData.filter((loc) => loc.province === event.province).length;

    // Update filters
    this.form.patchValue(
      {
        originProvince: event.province,
        destinationProvince: this.selectedDestinationProvince,
      },
      { emitEvent: true }
    );

    // Filter high claim routes
    this.filterHighClaimRoutesByProvince(event.province);
  }

  private filterHighClaimRoutesByProvince(province: string): void {
    if (!this.regionData?.highClaimRoutes) return;

    // Filter the high claim routes that have this province as origin or destination
    this.highClaimRoutesByProvince = this.regionData.highClaimRoutes
      .filter((route) => route.route.origin.province === province || route.route.destination.province === province)
      .map((route) => {
        // Use proper type casting with an interface that combines both types
        const latestData = route.data[route.data.length - 1] as RouteClaimsData;

        return {
          origin: `${route.route.origin.city}, ${route.route.origin.province}`,
          destination: `${route.route.destination.city}, ${route.route.destination.province}`,
          totalShipments: latestData.totalShipments,
          totalClaims: latestData.totalClaims,
          claimRate: latestData.claimRate,
          onTimePercentage: latestData.onTimePercentage,
          isOrigin: route.route.origin.province === province,
          riskLevel: this.getClaimRiskLevel(latestData.claimRate),
        };
      });

    // Calculate province-level claim statistics
    this.calculateProvinceClaimStats();
  }

  public getRouteFromDisplay(displayRoute: any): TopPerformingRoute {
    // Parse city and province from strings
    const [originCity, originProvince] = displayRoute.origin.split(', ');
    const [destCity, destProvince] = displayRoute.destination.split(', ');

    // First check if it's in highClaimRoutes
    const highClaimRoute = this.regionData.highClaimRoutes?.find(
      (route) =>
        route.route.origin.city === originCity &&
        route.route.origin.province === originProvince &&
        route.route.destination.city === destCity &&
        route.route.destination.province === destProvince
    );

    if (highClaimRoute) {
      // Convert HighClaimRoute to TopPerformingRoute format
      const convertedRoute: TopPerformingRoute = {
        route: highClaimRoute.route,
        data: highClaimRoute.data.map((item) => {
          // Create proper BucketMetrics object
          return {
            timeBucket: 'Unknown', // HighClaimRoute might not have this
            totalShipments: 0, // Default value, will be overridden if present
            onTimePercentage: 0, // Default value, will be overridden if present
            averageDelay: 0, // Default value, will be overridden if present
            totalClaims: item.totalClaims,
            claimRate: item.claimRate,
            // Add other defaults as needed
            ...item, // Spread the original item to override defaults if properties exist
          };
        }),
      };
      return convertedRoute;
    }

    // If not found, look in topPerformingRoutes
    const topRoute = this.regionData.topPerformingRoutes.find(
      (route) =>
        route.route.origin.city === originCity &&
        route.route.origin.province === originProvince &&
        route.route.destination.city === destCity &&
        route.route.destination.province === destProvince
    );

    return topRoute;
  }

  private calculateProvinceClaimStats(): void {
    if (!this.highClaimRoutesByProvince.length) {
      this.provinceClaimStats = {
        totalClaims: 0,
        claimRate: 0,
        highRiskRoutesCount: 0,
      };
      return;
    }

    let totalClaims = 0;
    let totalShipments = 0;
    let highRiskCount = 0;

    this.highClaimRoutesByProvince.forEach((route) => {
      totalClaims += route.totalClaims;
      totalShipments += route.totalShipments;
      if (route.riskLevel === 'high') highRiskCount++;
    });

    this.provinceClaimStats = {
      totalClaims,
      claimRate: totalShipments > 0 ? (totalClaims / totalShipments) * 100 : 0,
      highRiskRoutesCount: highRiskCount,
    };
  }

  public onRequestDetailData(event: {
    province: string;
    bounds: [[number, number], [number, number]];
    zoomLevel: number;
  }): void {
    // Generate detailed data for the province
    this.generateDetailedData(event.province, event.zoomLevel);
  }

  private generateDetailedData(province: string, zoomLevel: number): void {
    // Use metrics data to create detailed locations for the province
    const relevantMetrics = this.regionData.metrics.filter(
      (metric) => metric.origin.province === province || metric.destination.province === province
    );

    if (relevantMetrics.length === 0) {
      return;
    }

    // Create detailed location data
    const detailedLocations: LocationData[] = [];

    // Base coordinates for the province
    const baseCoords = OtpRegionsComponent.PROVINCE_COORDINATES[province];
    if (!baseCoords) return;

    // Process origin-destination pairs
    relevantMetrics.forEach((metric, index) => {
      const latestData = metric.data[metric.data.length - 1];
      const cityName = metric.origin.province === province ? metric.origin.city : metric.destination.city;

      // Create a slight offset to avoid overlapping points
      const offsetFactor = 0.2 / Math.max(1, zoomLevel / 2);
      const offsetLng = ((index % 5) - 2) * offsetFactor;
      const offsetLat = (Math.floor(index / 5) - 2) * offsetFactor;

      // Add metadata
      const metadata = {
        onTimePercentage: latestData.onTimePercentage,
        averageDelay: latestData.averageDelay,
        totalShipments: latestData.totalShipments,
        origin: `${metric.origin.city}, ${metric.origin.province}`,
        destination: `${metric.destination.city}, ${metric.destination.province}`,
      };

      // Add claims data if available
      if (latestData.claims) {
        metadata['totalClaims'] = latestData.claims.totalClaims;
        metadata['claimRate'] = latestData.claims.claimRate;
      }

      detailedLocations.push({
        latitude: baseCoords.lat + offsetLat,
        longitude: baseCoords.lng + offsetLng,
        volume: latestData.totalShipments,
        name: cityName,
        province: province,
        metadata: metadata,
      });
    });

    // Update detailed data in the heatmap
    this.detailedLocationData = detailedLocations;
    if (this.heatmapComponent) {
      this.heatmapComponent.setDetailedData(detailedLocations);
    }
  }

  /**
   * Route analysis methods
   */
  public showRouteDetails(route: TopPerformingRoute): void {
    this.showingRouteDetails = true;
    this.currentRouteMetrics = this.findRouteMetrics(route.route.origin, route.route.destination);

    if (this.currentRouteMetrics) {
      this.processRoutePerformance(this.currentRouteMetrics);
    }

    setTimeout(() => {
      const element = document.getElementById('routeDetailsSection');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 300);
  }

  private findRouteMetrics(origin: RegionLocation, destination: RegionLocation): RegionMetricsResponse | null {
    return (
      this.regionData.metrics.find(
        (metric) =>
          metric.origin.city === origin.city &&
          metric.origin.province === origin.province &&
          metric.destination.city === destination.city &&
          metric.destination.province === destination.province
      ) || null
    );
  }

  // Performance visualization data
  public routeOnTimeData: any[] = [];
  public routeVolumeData: any[] = [];
  public routeClaimsData: any[] = [];

  private processRoutePerformance(routeMetrics: RegionMetricsResponse): void {
    // Process route performance data for visualization
    this.currentRoutePerformance = routeMetrics.data.map((item) => ({
      name: item.timeBucket,
      onTimePercentage: item.onTimePercentage,
      totalShipments: item.totalShipments,
      averageDelay: item.averageDelay,
      bestCourier: item.bestCourier ? this.helpersService.convertShipmentProviderToString(item.bestCourier) : 'N/A',
      worstCourier: item.worstCourier ? this.helpersService.convertShipmentProviderToString(item.worstCourier) : 'N/A',
      totalClaims: item.claims?.totalClaims,
      claimRate: item.claims?.claimRate,
    }));

    // Prepare data for charts
    this.prepareRouteChartData();
  }

  private prepareRouteChartData(): void {
    // Prepare on-time percentage chart data
    this.routeOnTimeData = [
      {
        name: 'On-Time Percentage',
        series: this.currentRoutePerformance.map((item) => ({
          name: item.name,
          value: item.onTimePercentage,
        })),
      },
    ];

    // Prepare volume chart data
    this.routeVolumeData = this.currentRoutePerformance.map((item) => ({
      name: item.name,
      value: item.totalShipments,
    }));

    // Prepare claims chart data if available
    if (this.hasClaimsData && this.currentRoutePerformance.some((item) => item.claimRate !== undefined)) {
      this.routeClaimsData = [
        {
          name: 'Claim Rate',
          series: this.currentRoutePerformance
            .filter((item) => item.claimRate !== undefined)
            .map((item) => ({
              name: item.name,
              value: item.claimRate,
            })),
        },
      ];
    } else {
      this.routeClaimsData = [];
    }
  }

  public closeRouteDetails(): void {
    this.showingRouteDetails = false;
    this.currentRouteMetrics = null;
    this.currentRoutePerformance = [];
  }

  // Get province risk level description
  public getProvinceRiskLevel(claimRate: number): string {
    if (claimRate < 0.5) return 'Low Risk';
    if (claimRate < 1.0) return 'Medium Risk';
    return 'High Risk';
  }

  // Get CSS class for province risk
  public getClaimRiskClass(claimRate: number): string {
    if (claimRate < 0.5) return 'low-risk';
    if (claimRate < 1.0) return 'medium-risk';
    return 'high-risk';
  }

  // Get claim risk level
  public getClaimRiskLevel(rate: number): 'low' | 'medium' | 'high' {
    if (rate < 0.5) return 'low';
    if (rate < 1.0) return 'medium';
    return 'high';
  }

  // Methods to handle showing details
  public showHighClaimRouteDetails(route: any): void {
    // Convert to TopPerformingRoute format
    const convertedRoute: TopPerformingRoute = {
      route: route.route,
      data: route.data.map((item) => ({
        timeBucket: 'Current', // Default value or derive from other metrics
        totalShipments: item.totalShipments || 0,
        onTimePercentage: item.onTimePercentage || 0,
        averageDelay: item.averageDelay || 0,
        totalClaims: item.totalClaims,
        claimRate: item.claimRate,
      })),
    };

    this.showRouteDetails(convertedRoute);
  }

  public showHighVolumeRouteDetails(route: HighVolumeRoute): void {
    // Convert to TopPerformingRoute format if needed
    const convertedRoute: TopPerformingRoute = {
      route: route.route,
      data: route.data,
    };

    this.showRouteDetails(convertedRoute);
  }

  /**
   * Helper methods for formatting and display
   */
  // Helper to format percentage ticks for y-axis
  public formatYAxisPercentageTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toFixed(1) + '%';
    }
    return val;
  }

  // Helper to format number ticks for y-axis
  public formatYAxisNumberTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toLocaleString();
    }
    return val;
  }

  // Helper to format small percentage ticks for claims rate
  public formatClaimRateTicks(val: any): string {
    if (typeof val === 'number') {
      return val.toFixed(2) + '%';
    }
    return val;
  }

  // Helper to get trend icon based on direction
  public getTrendIcon(trend: TrendDirection): string {
    switch (trend) {
      case TrendDirection.IMPROVING:
        return 'trending_up';
      case TrendDirection.DECLINING:
        return 'trending_down';
      case TrendDirection.STABLE:
      default:
        return 'trending_flat';
    }
  }

  // Helper to get trend class
  public getTrendClass(trend: TrendDirection, isInverse: boolean = false): string {
    // For metrics where lower is better (like claim rate), the trend logic is inverted
    if (isInverse) {
      if (trend === TrendDirection.IMPROVING) return 'positive-trend';
      if (trend === TrendDirection.DECLINING) return 'negative-trend';
    } else {
      if (trend === TrendDirection.IMPROVING) return 'positive-trend';
      if (trend === TrendDirection.DECLINING) return 'negative-trend';
    }
    return 'stable-trend';
  }

  // Helper to get color for claim rate
  public getClaimRateColor(rate: number): string {
    if (rate === undefined || rate === null) return '#6c757d'; // Gray for N/A
    if (rate === 0) return '#6c757d'; // Gray for zero
    if (rate < 0.5) return '#28a745'; // Green for low
    if (rate < 1.0) return '#ffc107'; // Yellow for medium
    return '#dc3545'; // Red for high
  }

  // Helper to format claim rate for display
  public formatClaimRate(rate: number): string {
    if (rate === undefined || rate === null) return 'N/A';
    return rate.toFixed(2) + '%';
  }

  // Handle error states
  public handleError(): void {
    this.error = '';
    this.loadRegionData();
  }
}
